import {
  DataProvider,
  Filtering,
  Pagination,
  QBodyLayout,
  QButtonGroup,
  QHeader,
  QTitle,
  Sorting,
  useCurrentUser,
} from '@qualio/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../api/document';
import { DocumentStatus, QualioDocument } from '../../../../api/model/document';
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle';
import { usePersistanceKey } from '../../DocumentWorkspace/WorkspaceV2/hooks/usePersistanceKey';
import { ExportButton } from './ExportButton';
import { getLibraryTableParams } from './Filter/LibraryTableFilters';
import { useLibraryFilterDefinitions } from './hooks/useLibraryFilterDefinitions';
import { LibraryV2Table } from './LibraryV2Table';

export const LibraryV2: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { Provider: PaginationProvider } = Pagination.useLimitOffset();
  const filterDefinitions = useLibraryFilterDefinitions();
  const persistanceKey = usePersistanceKey();

  useDocumentTitle('Library');

  const fetchDocuments = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<QualioDocument>> => {
    const {
      offset,
      limit,
      orderBy,
      searchTerm,
      tagsFilter,
      templatesFilter,
      ownerFilter,
      forTraining,
    } = getLibraryTableParams(pageParam);

    const fetchMethod = forTraining
      ? documentApi.fetchUserTrainingDocs
      : documentApi.fetchAll;

    const { total: itemCount, documents: data } = await fetchMethod(
      currentUser,
      [DocumentStatus.Effective],
      searchTerm,
      tagsFilter,
      offset * limit,
      limit,
      orderBy,
      templatesFilter,
      ownerFilter,
    );

    return {
      itemCount,
      data: data ?? [],
    };
  };

  return (
    <PaginationProvider>
      <Filtering.FilterProvider
        searchTermKey="query"
        definitions={filterDefinitions}
        persistanceKey={persistanceKey}
      >
        <Sorting.DefaultSortingProvider sortByKey="order_by">
          <DataProvider.Remote
            queryFn={fetchDocuments}
            queryKey={['getDocuments', currentUser.userId]}
            queryParamKeys={{
              searchTerm: 'query',
              sortBy: 'order_by',
              pageSize: 'limit',
              pageIndex: 'offset',
            }}
          >
            <QBodyLayout.Default>
              <QHeader showDivider={false}>
                <QTitle data-cy="library-heading">{t('labels.library')}</QTitle>
                <QButtonGroup>
                  <ExportButton />
                </QButtonGroup>
              </QHeader>
              <LibraryV2Table />
            </QBodyLayout.Default>
          </DataProvider.Remote>
        </Sorting.DefaultSortingProvider>
      </Filtering.FilterProvider>
    </PaginationProvider>
  );
};
